import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { REGISTRATION_URL, SITE, LOGO_URL } from "@/lib/config";

const EVENTS_SUBMENU = [
    { to: "/eventos", label: "Todos los eventos" },
    {
        to: "/eventos/jornada-networking",
        label: "I Jornada de Networking",
        badge: "30 sept",
    },
];

const links = [
    { to: "/", label: "Inicio" },
    { to: "/institucional", label: "Institucional" },
    { to: "/noticias", label: "Noticias" },
    { to: "/eventos", label: "Eventos", submenu: EVENTS_SUBMENU },
    { to: "/directorio", label: "Directorio" },
    { to: "/area-socios", label: "Área de socios" },
    { to: "/contacto", label: "Contacto" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    const eventsActive = location.pathname.startsWith("/eventos");

    return (
        <header
            data-testid="site-header"
            className="fixed top-0 inset-x-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#082050]/10"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
                <Link
                    to="/"
                    data-testid="logo-link"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3"
                    aria-label={`${SITE.fullName} — Ir al inicio`}
                >
                    <img
                        src={LOGO_URL}
                        alt={`Logo oficial de ${SITE.fullName}`}
                        className="h-9 sm:h-10 md:h-11 w-auto"
                        width="200"
                        height="49"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {links.map((l) => {
                        if (l.submenu) {
                            return (
                                <div key={l.to} className="relative group">
                                    <NavLink
                                        to={l.to}
                                        data-testid={`nav-${l.to.replace("/", "") || "home"}`}
                                        className={`text-sm inline-flex items-center gap-1 transition-colors ${
                                            eventsActive
                                                ? "text-[#082050] font-medium"
                                                : "text-[#4A5568] hover:text-[#082050]"
                                        }`}
                                    >
                                        {l.label}
                                        <ChevronDown
                                            size={13}
                                            className="opacity-60 group-hover:rotate-180 transition-transform"
                                        />
                                    </NavLink>
                                    <div
                                        className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all"
                                        data-testid="events-submenu"
                                    >
                                        <div className="min-w-[240px] bg-white border border-[#082050]/10 shadow-lg py-2">
                                            {l.submenu.map((s) => (
                                                <Link
                                                    key={s.to}
                                                    to={s.to}
                                                    data-testid={`nav-sub-${s.to.split("/").pop()}`}
                                                    className="flex items-center justify-between gap-3 px-5 py-3 text-sm text-[#4A5568] hover:text-[#082050] hover:bg-[#082050]/5"
                                                >
                                                    <span>{s.label}</span>
                                                    {s.badge && (
                                                        <span className="text-[10px] uppercase tracking-widest text-[#082050]/70 border border-[#082050]/30 px-2 py-0.5">
                                                            {s.badge}
                                                        </span>
                                                    )}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-${l.to.replace("/", "") || "home"}`}
                                className={({ isActive }) =>
                                    `text-sm transition-colors ${
                                        isActive
                                            ? "text-[#082050] font-medium"
                                            : "text-[#4A5568] hover:text-[#082050]"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <a
                        href={REGISTRATION_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="btn-hazte-socio-header"
                        className="bg-[#082050] text-white px-6 py-3 text-sm font-medium hover:bg-[#082050]/90 transition-transform hover:-translate-y-0.5"
                    >
                        Hazte socio · {SITE.quota}
                    </a>
                </div>

                <button
                    data-testid="mobile-menu-btn"
                    aria-label={open ? "Cerrar menú" : "Abrir menú"}
                    aria-expanded={open}
                    className="lg:hidden inline-flex items-center justify-center w-10 h-10 text-[#082050]"
                    onClick={() => setOpen((v) => !v)}
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden border-t border-[#082050]/10 bg-white max-h-[calc(100dvh-4rem)] overflow-y-auto"
                >
                    <nav className="px-4 py-4 flex flex-col">
                        {links.map((l) => (
                            <React.Fragment key={l.to}>
                                <NavLink
                                    to={l.to}
                                    onClick={() => setOpen(false)}
                                    data-testid={`nav-mobile-${l.to.replace("/", "") || "home"}`}
                                    className={({ isActive }) =>
                                        `text-base py-3 border-b border-[#082050]/5 ${
                                            isActive
                                                ? "text-[#082050] font-medium"
                                                : "text-[#4A5568]"
                                        }`
                                    }
                                >
                                    {l.label}
                                </NavLink>
                                {l.submenu &&
                                    l.submenu
                                        .filter((s) => s.to !== l.to)
                                        .map((s) => (
                                            <Link
                                                key={s.to}
                                                to={s.to}
                                                onClick={() => setOpen(false)}
                                                data-testid={`nav-mobile-sub-${s.to.split("/").pop()}`}
                                                className="pl-4 py-2.5 text-sm text-[#4A5568] flex items-center gap-2"
                                            >
                                                <span className="h-px w-3 bg-[#082050]/20" />
                                                {s.label}
                                                {s.badge && (
                                                    <span className="text-[10px] uppercase tracking-widest text-[#082050]/70 border border-[#082050]/30 px-1.5 py-0.5">
                                                        {s.badge}
                                                    </span>
                                                )}
                                            </Link>
                                        ))}
                            </React.Fragment>
                        ))}
                        <a
                            href={REGISTRATION_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="btn-hazte-socio-mobile-menu"
                            className="mt-4 bg-[#082050] text-white px-6 py-4 text-sm font-medium text-center"
                        >
                            Hazte socio · {SITE.quota}
                        </a>
                    </nav>
                </div>
            )}
        </header>
    );
}
